"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFormInputController = void 0;
var react_1 = require("react");
var react_hook_form_1 = require("react-hook-form");
var Form_1 = require("../Form");
/**
 * Used to connect an input component to the react-hook-form
 * @typeParam TFormSchema - typescript interface of your form
 * @typeParam TFormField - a string path that matches this form input name and must be contained in your TFormSchema
 * @param name - name of the form field
 * @param onChange - optional callback from the underlying input
 * @param onBlur - optional callback from the underlying input
 * @example
 * ```
 * const controller = useFormInputController<{foo: {bar: string[], baz: number[]}}, 'foo.bar'>
 * <FormInput {...controller}
 * ```
 * @returns
 * ```
 * {
 *   value: any
 *   onChange: function
 *   onBlur: function
 *   error: string
 *   isRequired: boolean
 *   ref: React.Ref
 * }
 * ```
 */
var useFormInputController = function (_a) {
    var name = _a.name, optionalOnChange = _a.onChange, optionalOnBlur = _a.onBlur;
    var _b = (0, react_hook_form_1.useController)({ name: name }), _c = _b.field, value = _c.value, onChange = _c.onChange, onBlur = _c.onBlur, ref = _c.ref, error = _b.fieldState.error;
    var formSchema = (0, Form_1.useFormSchema)();
    var isRequired = (0, react_1.useMemo)(function () {
        var _a, _b;
        //  @ts-ignore Yup's BaseSchema interface doesn't provide 'spec' as a public field. This is the only way to determine the required state
        var schemaSpec = (_b = (_a = formSchema === null || formSchema === void 0 ? void 0 : formSchema.fields[name]) === null || _a === void 0 ? void 0 : _a.spec) === null || _b === void 0 ? void 0 : _b.presence;
        return schemaSpec === 'required';
    }, [formSchema, name]);
    return (0, react_1.useMemo)(function () {
        return {
            value: value,
            onChange: optionalOnChange
                ? function (changeEvent, changeValue) {
                    onChange(changeEvent, changeValue);
                    optionalOnChange(changeEvent, changeValue);
                }
                : onChange,
            onBlur: optionalOnBlur
                ? function (blurEvent) {
                    onBlur();
                    optionalOnBlur(blurEvent);
                }
                : onBlur,
            error: error === null || error === void 0 ? void 0 : error.message,
            isRequired: isRequired,
            ref: ref,
        };
    }, [value, onChange, optionalOnChange, onBlur, optionalOnBlur, error, isRequired, ref]);
};
exports.useFormInputController = useFormInputController;
